import React, { Suspense, lazy, useEffect } from 'react';
import { Container } from './theme';
import Navbar from './components/layout/Navbar';
import Hero from './components/sections/Hero';
import FloatingWhatsApp from './components/layout/FloatingWhatsApp';
import PromoModal from './components/promo/PromoModal';
import SEOHead from './components/shared/SEOHead';
import { ReviewsStructuredData, ServicesStructuredData, FAQStructuredData } from './components/shared/StructuredData';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { ToastContainer } from './utils/toast';
import { useSharedContent } from './providers/ContentProvider';
import { useSmoothScroll } from './hooks/ui/useSmoothScroll';
import { useVisitorTracking } from './hooks/useVisitorTracking';

const ProductView = lazy(async () => import('./components/sections/ProductView'));
const WhyChooseUs = lazy(async () => import('./components/sections/WhyChooseUs'));
const About = lazy(async () => import('./components/sections/About'));
const Team = lazy(async () => import('./components/sections/Team'));
const ImagesCarousel = lazy(async () => import('./components/carousel/ImagesCarousel'));
const Contact = lazy(async () => import('./components/contact/Contact'));
const Reviews = lazy(async () => import('./components/reviews/Reviews'));
const MobileQuickContact = lazy(async () => import('./components/layout/MobileQuickContact'));

interface SectionFallbackProps {
  label: string;
  loadingText?: string;
}

const SectionFallback: React.FC<SectionFallbackProps> = ({ label, loadingText }) => {
  return (
    <div className="py-16 flex flex-col items-center justify-center gap-4 text-ink-muted" aria-live="polite">
      <div className="h-12 w-12 rounded-full border-4 border-border border-t-brand animate-spin" />
      <p className="menu-font text-sm uppercase tracking-[0.35em] text-brand">
        {loadingText || "טוען"} {label}
      </p>
    </div>
  );
};

export default function App(): React.ReactElement {
  // Fetch content from database (falls back to local JSON if not configured)
  const { content, loading } = useSharedContent();
  useSmoothScroll();
  useVisitorTracking(); // Track visitor statistics

  // After content mounts (including lazy sections), ensure hash targets are scrolled into view
  useEffect(() => {
    if (loading) {
      return;
    }

    let cancelled = false;
    const timeouts: number[] = [];

    const attemptScroll = (hash: string, attempt: number = 0): void => {
      if (cancelled || !hash) {
        return;
      }
      const el = document.querySelector(hash);
      if (el) {
        (el as HTMLElement).scrollIntoView({ behavior: 'auto', block: 'start' });
        return;
      }
      if (attempt > 10) {
        return;
      }
      const timeoutId = window.setTimeout(() => attemptScroll(hash, attempt + 1), 150);
      timeouts.push(timeoutId);
    };

    const handleHashNavigation = (): void => {
      const hash = window.location.hash;
      if (!hash) {
        return;
      }
      attemptScroll(hash);
    };

    handleHashNavigation();
    window.addEventListener('hashchange', handleHashNavigation);

    return () => {
      cancelled = true;
      window.removeEventListener('hashchange', handleHashNavigation);
      timeouts.forEach((id) => window.clearTimeout(id));
    };
  }, [loading]);

  // Show loading state
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          <div>Loading content...</div>
        </div>
      </div>
    );
  }

  // Show error if any (but still render with fallback content)
  // Error is handled silently, app continues with fallback content
  return (
    <div id="top" className="min-h-screen body-font w-full overflow-x-hidden bg-surface-bg text-ink">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:right-2 focus:z-[9999] focus:bg-brand focus:text-white focus:px-4 focus:py-2 focus:rounded-lg">
        דלג לתוכן הראשי
      </a>
      <SEOHead
        title="Touch Event | עיסוי רפואי לאירועים ומשרדים"
        description="Touch Event - שירותי עיסוי רפואי מקצועי לאירועים, חברות ומשרדים. עיסוי במקום העבודה, שחרור שרירים, רפלקסולוגיה, כוסות רוח וטיפולי רווחה לעובדים. ספורטתרפיסטים מוסמכים באזור המרכז. הזמינו עכשיו!"
        path="/"
      />
      <ReviewsStructuredData reviews={content.reviews} />
      <ServicesStructuredData gallery={content.gallery} />
      <FAQStructuredData why={content.why} />
      <ToastContainer />
      
      {/* Promo Modal */}
      {content.promoModal && (
        <ErrorBoundary>
          <PromoModal content={content.promoModal} />
        </ErrorBoundary>
      )}
      
      <ErrorBoundary>
        <Navbar brand={content.brand} logo={content.logo} nav={content.nav} social={content.social} />
      </ErrorBoundary>
      <main id="main-content" role="main" className="pt-16">
        <ErrorBoundary>
          <Hero content={content.hero} />
        </ErrorBoundary>
        <Suspense fallback={<SectionFallback label="אודות" loadingText={content.ui?.loading?.loadingLabel} />}>
          <ErrorBoundary>
            <About about={content.about} />
          </ErrorBoundary>
        </Suspense>
        <Suspense fallback={<SectionFallback label="המוצרים" loadingText={content.ui?.loading?.loadingLabel} />}>
          <ErrorBoundary>
            <ProductView gallery={content.gallery} />
          </ErrorBoundary>
        </Suspense>
        <Suspense fallback={<SectionFallback label="למה לבחור בנו" loadingText={content.ui?.loading?.loadingLabel} />}>
          <ErrorBoundary>
            <WhyChooseUs why={content.why} />
          </ErrorBoundary>
        </Suspense>
        <Suspense fallback={<SectionFallback label="הצוות" loadingText={content.ui?.loading?.loadingLabel} />}>
          <ErrorBoundary>
            <Team team={content.team} />
          </ErrorBoundary>
        </Suspense>
        <Suspense fallback={<SectionFallback label="קרוסלת תמונות" loadingText={content.ui?.loading?.loadingLabel} />}>
          <ErrorBoundary>
            <ImagesCarousel 
              items={content.carousel} 
              title={content.carouselTitle} 
              description={content.carouselDescription}
              loadingText={content.ui?.loading?.loadingImages}
            />
          </ErrorBoundary>
        </Suspense>
        <Suspense fallback={<SectionFallback label="ביקורות" loadingText={content.ui?.loading?.loadingLabel} />}>
          <ErrorBoundary>
            <Reviews reviews={content.reviews} />
          </ErrorBoundary>
        </Suspense>
        <Suspense fallback={<SectionFallback label="צור קשר" loadingText={content.ui?.loading?.loadingLabel} />}>
          <ErrorBoundary>
            <Contact contact={content.contact} />
          </ErrorBoundary>
        </Suspense>
        
        {/* Floating WhatsApp Button - Mobile Only */}
        <ErrorBoundary>
          <FloatingWhatsApp contact={content.contact} />
        </ErrorBoundary>
        
        <Suspense fallback={<SectionFallback label="קיצור ליצירת קשר" loadingText={content.ui?.loading?.loadingLabel} />}>
          <ErrorBoundary>
            <MobileQuickContact contact={content.contact} social={content.social} navLabels={content.nav} legal={content.legal} mobileQuickContact={content.mobileQuickContact} />
          </ErrorBoundary>
        </Suspense>
      </main>
      <footer role="contentinfo" className="py-4 md:py-10 border-t border-border mt-10 bg-surface">
        <Container>
          {/* Legal Links - Hidden on mobile since they appear in MobileQuickContact */}
          <div className="hidden md:flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 mb-6 text-sm" dir="rtl">
            <span className="text-brand font-semibold">{content.legal?.footerLabel || "מידע משפטי:"}</span>
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
              <a href="/privacy" className="text-ink-muted hover:text-brand transition-colors">
                {content.legal?.privacyTitle || "מדיניות פרטיות"}
              </a>
              <span className="text-border">|</span>
              <a href="/terms" className="text-ink-muted hover:text-brand transition-colors">
                {content.legal?.termsTitle || "תנאי שימוש"}
              </a>
              <span className="text-border">|</span>
              <a href="/accessibility" className="text-ink-muted hover:text-brand transition-colors">
                {content.legal?.accessibilityTitle || "הצהרת נגישות"}
              </a>
            </div>
          </div>
          
          {/* Copyright & Back to Top */}
          <div className="flex items-center justify-between md:pt-4 md:border-t md:border-border/50">
            <div className="text-sm text-ink-muted">© {new Date().getFullYear()} {content.brand}. {content.ui?.footer?.copyright || "All rights reserved"}</div>
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="text-sm menu-font hover:opacity-80 transition-opacity cursor-pointer"
            >
              {content.ui?.footer?.backToTop || "Back to top ↑"}
            </button>
          </div>
        </Container>
      </footer>
    </div>
  );
}
